import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import ReactDOM from 'react-dom';
import { useConsent } from '../../context/ConsentContext';
import { PrimaryButton, SecondaryButton } from '../common/Button';

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(3px);
    z-index: 10000010;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
`;

const Panel = styled.div`
    background: ${(p) => p.theme.colors.background};
    color: ${(p) => p.theme.colors.text};
    border-radius: 14px;
    width: 100%;
    max-width: 560px;
    max-height: 90vh;
    overflow-y: auto;
    padding: 24px;
    box-shadow: 0 16px 48px rgba(0, 0, 0, 0.4);
`;

const Title = styled.h2`
    margin: 0 0 4px;
    font-size: 18px;
    font-weight: 700;
`;

const Intro = styled.p`
    margin: 0 0 16px;
    font-size: 14px;
    line-height: 1.5;
    opacity: 0.85;
`;

const Row = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 14px 0;
    border-top: 1px solid ${(p) => p.theme.colors.border};

    &:last-of-type {
        border-bottom: 1px solid ${(p) => p.theme.colors.border};
    }
`;

const RowText = styled.div`
    flex: 1;
    h4 {
        margin: 0 0 4px;
        font-size: 14px;
        font-weight: 600;
    }
    p {
        margin: 0;
        font-size: 13px;
        line-height: 1.4;
        opacity: 0.8;
    }
`;

const Switch = styled.label`
    position: relative;
    display: inline-block;
    width: 40px;
    height: 22px;
    flex-shrink: 0;

    input {
        opacity: 0;
        width: 0;
        height: 0;
    }

    span {
        position: absolute;
        cursor: pointer;
        inset: 0;
        background: ${(p) => p.theme.colors.border};
        border-radius: 22px;
        transition: 0.2s;

        &::before {
            content: '';
            position: absolute;
            height: 16px;
            width: 16px;
            left: 3px;
            top: 3px;
            background: white;
            border-radius: 50%;
            transition: 0.2s;
        }
    }

    input:checked + span {
        background: ${(p) => p.theme.colors.primary};
    }
    input:checked + span::before {
        transform: translateX(18px);
    }
    input:disabled + span {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const Footer = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 18px;
    flex-wrap: wrap;
`;

const DEFAULT_CATEGORIES = [
    { id: 'necessary', name: 'Strictly Necessary', required: true, description: 'Required for the site to function: authentication, security, load balancing, and remembering your consent choice.' },
    { id: 'analytics', name: 'Analytics', required: false, description: 'Helps us understand how the product is used so we can improve it.' },
    { id: 'marketing', name: 'Marketing', required: false, description: 'Used to deliver and measure ads on this site and other platforms.' },
    { id: 'personalization', name: 'Personalization', required: false, description: 'Tailors content and recommendations to your interests.' },
];

function categoriesFromPolicy(policy) {
    const list = policy?.categories;
    if (Array.isArray(list) && list.length) return list;
    return DEFAULT_CATEGORIES;
}

export default function ConsentPreferenceModal() {
    const { isPreferencesOpen, closePreferences, consent, policy, accept } = useConsent();
    const categories = useMemo(() => categoriesFromPolicy(policy), [policy]);
    const [state, setState] = useState({});

    useEffect(() => {
        if (!isPreferencesOpen) return;
        const initial = {};
        for (const cat of categories) {
            const id = cat.id || cat.key;
            const required = !!cat.required;
            const existing = consent?.categories?.[id];
            initial[id] = required ? true : !!existing;
        }
        setState(initial);
    }, [isPreferencesOpen, categories, consent]);

    if (!isPreferencesOpen) return null;

    const onToggle = (id) => (e) => {
        setState((s) => ({ ...s, [id]: e.target.checked }));
    };

    const onSave = async () => {
        await accept(state, 'preference_center');
        closePreferences();
    };

    const onAcceptAll = async () => {
        const all = {};
        for (const cat of categories) all[cat.id || cat.key] = true;
        await accept(all, 'preference_center');
        closePreferences();
    };

    const onRejectAll = async () => {
        const none = {};
        for (const cat of categories) {
            const id = cat.id || cat.key;
            none[id] = !!cat.required;
        }
        await accept(none, 'preference_center');
        closePreferences();
    };

    const copy = policy?.copy?.en || {};

    return ReactDOM.createPortal(
        <Overlay role="dialog" aria-modal="true" aria-label="Cookie preferences" onClick={closePreferences}>
            <Panel onClick={(e) => e.stopPropagation()}>
                <Title>{copy.bannerTitle || 'Cookie preferences'}</Title>
                <Intro>
                    {copy.bannerBody ||
                        'Choose which cookies you want to allow. You can change these settings anytime from the footer.'}
                </Intro>

                {categories.map((cat) => {
                    const id = cat.id || cat.key;
                    return (
                        <Row key={id}>
                            <RowText>
                                <h4>{cat.name || id}{cat.required ? ' (always on)' : ''}</h4>
                                <p>{cat.description}</p>
                            </RowText>
                            <Switch>
                                <input
                                    type="checkbox"
                                    checked={!!state[id]}
                                    onChange={onToggle(id)}
                                    disabled={!!cat.required}
                                    aria-label={`Toggle ${cat.name || id}`}
                                />
                                <span />
                            </Switch>
                        </Row>
                    );
                })}

                <Footer>
                    <SecondaryButton type="button" onClick={onRejectAll}>
                        {copy.rejectAll || 'Reject all'}
                    </SecondaryButton>
                    <SecondaryButton type="button" onClick={onAcceptAll}>
                        {copy.acceptAll || 'Accept all'}
                    </SecondaryButton>
                    <PrimaryButton type="button" onClick={onSave}>
                        {copy.save || 'Save preferences'}
                    </PrimaryButton>
                </Footer>
            </Panel>
        </Overlay>,
        document.body
    );
}
